import React, { Component, ErrorInfo, ReactNode } from 'react';
import { MsalErrorFallback } from './MsalErrorFallback';

interface MsalErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface MsalErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class MsalErrorBoundary extends Component<MsalErrorBoundaryProps, MsalErrorBoundaryState> {
  constructor(props: MsalErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
    
    // Add global error handler to catch unhandled errors
    window.addEventListener('error', (event) => {
      if (event.error && event.error.message && 
          (event.error.message.includes('auth') || 
           event.error.message.includes('MSAL') || 
           event.error.message.includes('client_id'))) {
        this.setState({ 
          hasError: true, 
          error: event.error 
        });
        event.preventDefault();
      }
    });
    
    // Also listen for unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && 
          (event.reason.includes('auth') || 
           event.reason.includes('MSAL') || 
           event.reason.includes('client_id'))) {
        this.setState({ 
          hasError: true, 
          error: new Error(String(event.reason)) 
        });
        event.preventDefault();
      }
    });
  }

  static getDerivedStateFromError(error: Error): MsalErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to the console or your error reporting service
    console.error('MSAL Error caught by boundary:', error);
    console.error('Component stack:', errorInfo.componentStack);
  }
  
  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use the custom fallback if provided, otherwise use the default MsalErrorFallback
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <MsalErrorFallback 
          error={this.state.error} 
          resetErrorBoundary={this.resetErrorBoundary} 
        />
      );
    }

    // Otherwise, render the children
    return this.props.children;
  }
} 